import { AdminLayout } from "@/components/layout/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { trpc } from "@/lib/trpc";
import { formatDate } from "@/lib/utils";
import { Shield, Users, FileText, AlertCircle, CheckCircle2 } from "lucide-react";

export default function Rgpd() {
  const { data: stats, isLoading } = trpc.admin.dashboard.stats.useQuery();
  const { data: auditLogs, isLoading: auditLoading } = trpc.admin.audit.list.useQuery({
    page: 1,
    limit: 20,
  });

  const rgpdActions = [
    "USER_DELETE",
    "USER_ANONYMIZE",
    "DATA_EXPORT",
    "GDPR_REQUEST",
    "COMPANY_DELETE",
  ];

  const rgpdLogs = (auditLogs?.items ?? []).filter((log: any) =>
    rgpdActions.includes(log.action)
  );

  return (
    <AdminLayout>
      <div className="space-y-6 animate-fade-in">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">RGPD</h1>
          <p className="text-muted-foreground">Conformité réglementaire et gestion des données personnelles</p>
        </div>

        {/* Statut conformité */}
        <Card className="border-success/30">
          <CardContent className="flex items-center gap-4 p-6">
            <CheckCircle2 className="h-8 w-8 text-success" />
            <div>
              <div className="font-semibold text-lg">Plateforme conforme RGPD</div>
              <div className="text-sm text-muted-foreground">
                Audit de conformité en cours — données pseudonymisées, logs d'accès actifs
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Vue d'ensemble données */}
        <div>
          <h2 className="text-base font-semibold mb-3 flex items-center gap-2">
            <Users className="h-4 w-4" />
            Données personnelles en base
          </h2>
          <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Utilisateurs actifs</CardTitle>
                <Users className="h-4 w-4 text-primary" />
              </CardHeader>
              <CardContent>
                {isLoading ? <Skeleton className="h-8 w-12" /> : (
                  <>
                    <div className="text-2xl font-bold">{stats?.usersCount ?? 0}</div>
                    <p className="text-xs text-muted-foreground">Comptes clients avec données personnelles</p>
                  </>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Entreprises</CardTitle>
                <Shield className="h-4 w-4 text-primary" />
              </CardHeader>
              <CardContent>
                {isLoading ? <Skeleton className="h-8 w-12" /> : (
                  <>
                    <div className="text-2xl font-bold">{stats?.companiesCount ?? 0}</div>
                    <p className="text-xs text-muted-foreground">Tenants avec données entreprise</p>
                  </>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Demandes RGPD</CardTitle>
                <FileText className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{rgpdLogs.length}</div>
                <p className="text-xs text-muted-foreground">dans l'audit log</p>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Checklist conformité */}
        <div>
          <h2 className="text-base font-semibold mb-3">Checklist RGPD</h2>
          <div className="space-y-2">
            {[
              { label: "Politique de confidentialité publiée", status: "ok" },
              { label: "Consentement collecté à l'inscription", status: "ok" },
              { label: "Audit log activé (actions staff + users)", status: "ok" },
              { label: "Chiffrement des données en transit (HTTPS/TLS)", status: "ok" },
              { label: "Procédure de suppression/anonymisation utilisateur", status: "warning" },
              { label: "Export des données personnelles sur demande (Art. 20)", status: "warning" },
              { label: "DPO désigné et coordonnées publiées", status: "warning" },
              { label: "Durées de conservation définies par type de donnée", status: "todo" },
              { label: "Registre des traitements tenu à jour", status: "todo" },
            ].map((item) => (
              <div key={item.label} className="flex items-center justify-between p-3 border rounded-lg">
                <div className="flex items-center gap-3">
                  {item.status === "ok" ? (
                    <CheckCircle2 className="h-4 w-4 text-success flex-shrink-0" />
                  ) : item.status === "warning" ? (
                    <AlertCircle className="h-4 w-4 text-warning flex-shrink-0" />
                  ) : (
                    <AlertCircle className="h-4 w-4 text-destructive flex-shrink-0" />
                  )}
                  <span className="text-sm">{item.label}</span>
                </div>
                <Badge
                  variant={item.status === "ok" ? "success" : item.status === "warning" ? "outline" : "destructive"}
                  className={item.status === "warning" ? "border-warning/50 text-warning" : ""}
                >
                  {item.status === "ok" ? "Conforme" : item.status === "warning" ? "En cours" : "À faire"}
                </Badge>
              </div>
            ))}
          </div>
        </div>

        {/* Audit log RGPD */}
        <div>
          <h2 className="text-base font-semibold mb-3">Événements RGPD dans l'audit log</h2>
          <Card>
            <CardContent className="p-0">
              {auditLoading ? (
                <div className="p-6 space-y-3">
                  {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}
                </div>
              ) : rgpdLogs.length === 0 ? (
                <div className="p-8 text-center text-muted-foreground">
                  <Shield className="h-8 w-8 mx-auto mb-2 text-muted-foreground/40" />
                  <p>Aucun événement RGPD enregistré</p>
                  <p className="text-xs mt-1">Les suppressions, anonymisations et exports apparaîtront ici</p>
                </div>
              ) : (
                <div className="divide-y">
                  {rgpdLogs.map((log: any) => (
                    <div key={log.id} className="flex items-center justify-between p-4">
                      <div>
                        <div className="font-medium text-sm">{log.action}</div>
                        <div className="text-xs text-muted-foreground">
                          {log.User?.email ?? "Système"} · {formatDate(log.createdAt)}
                        </div>
                      </div>
                      <Badge variant="outline">{log.entityType}</Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </AdminLayout>
  );
}
